import Joi from 'joi'
import { useEffect } from 'react'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { joiResolver } from '@hookform/resolvers/joi'

import Btn from '../../../../../../UI/Btn'
import Checkbox from '../../../../../../UI/Checkbox'
import SelectField from '../../../../../../UI/SelectField'
import TextField from '../../../../../../UI/TextField'

import SettingHint from '../SettingHint'
import TemplateLinks from '../TemplateLinks'

import styles from '../style.module.scss'

/*
 * Muss zu services/reminders/ticketTodoSchedule.js im Backend passen. Steht
 * dort ein anderer Wert, plant der Job etwas anderes als hier angezeigt wird.
 */
const FREQUENCIES = [
	{ value: 'DAILY', label: 'täglich' },
	{ value: 'WEEKLY', label: 'wöchentlich' },
	{ value: 'BIWEEKLY', label: 'alle zwei Wochen' },
	{ value: 'MONTHLY', label: 'monatlich' }
]

// ISO-Zählung: 1 = Montag … 7 = Sonntag. Das Backend rechnet sie in die
// cron-Zählung um (dort ist 0 der Sonntag).
const WEEKDAYS = [
	{ value: 1, label: 'Montag' },
	{ value: 2, label: 'Dienstag' },
	{ value: 3, label: 'Mittwoch' },
	{ value: 4, label: 'Donnerstag' },
	{ value: 5, label: 'Freitag' },
	{ value: 6, label: 'Samstag' },
	{ value: 7, label: 'Sonntag' }
]

const DEFAULTS = {
	enabled: true,
	frequency: 'WEEKLY',
	weekday: 1,
	hour: 7,
	minute: 0,
	minOpenTodos: 2
}

/*
 * Meldungen auf Deutsch und feldbezogen. Ohne eigene Texte zeigt Joi den
 * Rohtext samt Feldpfad an („ticketTodoReminders.hour must be less than or
 * equal to 23") — englisch und für die Person vor der Maske nicht verwertbar.
 */
const schema = Joi.object({
	ticketTodoReminders: Joi.object().keys({
		enabled: Joi.boolean(),
		frequency: Joi.string()
			.valid(...FREQUENCIES.map(entry => entry.value))
			.required()
			.messages({ 'any.only': 'Bitte einen Takt auswählen.' }),
		weekday: Joi.number().integer().min(1).max(7).required().messages({
			'number.base': 'Bitte einen Wochentag auswählen.',
			'any.required': 'Bitte einen Wochentag auswählen.'
		}),
		hour: Joi.number().integer().min(0).max(23).required().messages({
			'number.base': 'Bitte eine Stunde zwischen 0 und 23 angeben.',
			'number.integer': 'Bitte eine ganze Stunde angeben.',
			'number.min': 'Bitte eine Stunde zwischen 0 und 23 angeben.',
			'number.max': 'Bitte eine Stunde zwischen 0 und 23 angeben.',
			'any.required': 'Bitte eine Stunde zwischen 0 und 23 angeben.'
		}),
		minute: Joi.number().integer().min(0).max(59).required().messages({
			'number.base': 'Bitte eine Minute zwischen 0 und 59 angeben.',
			'number.integer': 'Bitte eine ganze Minute angeben.',
			'number.min': 'Bitte eine Minute zwischen 0 und 59 angeben.',
			'number.max': 'Bitte eine Minute zwischen 0 und 59 angeben.',
			'any.required': 'Bitte eine Minute zwischen 0 und 59 angeben.'
		}),
		minOpenTodos: Joi.number().integer().min(1).max(99).required().messages({
			'number.base': 'Bitte eine Anzahl offener Aufgaben angeben.',
			'number.integer': 'Bitte eine ganze Zahl angeben.',
			'number.min': 'Bitte mindestens 1 angeben — bei 0 ginge eine Mail an alle.',
			'number.max': 'Bitte höchstens 99 angeben.',
			'any.required': 'Bitte eine Anzahl offener Aufgaben angeben.'
		})
	})
})

/**
 * Takt der Erinnerung an offene Aufgaben.
 *
 * Wem im Bereich Anforderungen mehrere Aufgaben zugewiesen sind, bekommt eine
 * kurze Übersicht per E-Mail — eine Sammelmail, nicht eine je Aufgabe. Der
 * Standard ist Montag 07:00: zum Wochenbeginn, bevor der Tag anfängt.
 *
 * Die Uhr läuft im Server, der Takt steht hier in der Datenbank. Nach dem
 * Speichern stellt sich der Job selbst neu — ein Neustart ist nicht nötig.
 */
export default function TicketTodoReminders(props) {
	const { t } = useTranslation()
	const {
		register,
		reset,
		control,
		handleSubmit,
		formState: { errors }
	} = useForm({ resolver: joiResolver(schema) })

	const onSubmit = form => {
		const value = form.ticketTodoReminders

		props
			.updateOptions({
				name: 'ticketTodoReminders',
				value: {
					enabled: Boolean(value.enabled),
					frequency: value.frequency,
					weekday: Number(value.weekday),
					hour: Number(value.hour),
					minute: Number(value.minute),
					minOpenTodos: Number(value.minOpenTodos)
				}
			})
			.then(() => toast.success(t('Options was updated')))
	}

	useEffect(() => {
		const value = props.form?.ticketTodoReminders || {}
		reset({ ticketTodoReminders: { ...DEFAULTS, ...value } })
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.form])

	return (
		<form id="ticketTodoReminders" className={styles.root} onSubmit={handleSubmit(onSubmit)}>
			<section className={styles.section}>
				<h3 className={styles.sectionTitle}>Erinnerung an offene Aufgaben</h3>
				<SettingHint
					example="Beispiel: wöchentlich, Montag, 07:00, ab 2 offenen Aufgaben — wer montags früh mehr als eine offene Aufgabe hat, findet die Übersicht im Postfach."
					warning="Die Mail geht nur an Personen, denen mindestens so viele Aufgaben zugewiesen sind wie unten angegeben. Wer weniger hat, bekommt nichts — eine Erinnerung an eine einzige Aufgabe wäre Post ohne Anlass. Wer sich unter „Mein Profil“ → „Erinnerungen per E-Mail“ die eigene Übersicht über zugewiesene Anforderungen eingestellt hat, bekommt diese hier nicht zusätzlich. Zeitangaben in Wiener Zeit."
				>
					Offene Aufgaben aus dem Bereich Anforderungen werden je Person zu einer kurzen Übersicht
					zusammengefasst und per E-Mail verschickt: Anzahl und die dringendsten fünf. Als offen zählt alles
					bis einschließlich „Zur Abnahme".
				</SettingHint>
				<TemplateLinks items={[{ channel: 'email', key: 'TICKET_TODO_DIGEST' }]} />
				<div className={styles.sectionContent}>
					<div className={styles.flexContainer}>
						<Checkbox label="Erinnerung verschicken" register={register('ticketTodoReminders.enabled')} />
					</div>

					<div className={styles.flexContainer}>
						<SelectField
							className={styles.field}
							label="Takt"
							placeholder={t('Please select')}
							options={FREQUENCIES}
							control={control}
							name="ticketTodoReminders.frequency"
							error={errors.ticketTodoReminders?.frequency}
						/>
						{/*
						 * Beim täglichen Takt ohne Wirkung — bewusst trotzdem
						 * sichtbar: Wer von täglich zurück auf wöchentlich stellt,
						 * soll seinen Wochentag noch vorfinden.
						 */}
						<SelectField
							className={styles.field}
							label="Wochentag"
							placeholder={t('Please select')}
							options={WEEKDAYS}
							control={control}
							name="ticketTodoReminders.weekday"
							error={errors.ticketTodoReminders?.weekday}
						/>
					</div>

					<div className={styles.flexContainer}>
						<TextField
							className={styles.field}
							label="Uhrzeit — Stunde (0–23)"
							mb={20}
							register={register('ticketTodoReminders.hour')}
							error={errors.ticketTodoReminders?.hour}
						/>
						<TextField
							className={styles.field}
							label="Uhrzeit — Minute (0–59)"
							mb={20}
							register={register('ticketTodoReminders.minute')}
							error={errors.ticketTodoReminders?.minute}
						/>
						<TextField
							className={styles.field}
							label="Erst ab [x] offenen Aufgaben erinnern"
							mb={20}
							register={register('ticketTodoReminders.minOpenTodos')}
							error={errors.ticketTodoReminders?.minOpenTodos}
						/>
					</div>

					<div className={styles.btnContainer}>
						<Btn
							className={styles.btn}
							type="submit"
							icon="arrow"
							iconSize={{ w: 10, h: 10 }}
							iconColor="#fff"
						>
							{t('save')}
						</Btn>
					</div>
				</div>
			</section>
		</form>
	)
}

TicketTodoReminders.propTypes = {
	form: PropTypes.object.isRequired,
	updateOptions: PropTypes.func.isRequired
}
